
export enum Role {
  BRAND = 'Brand',
  FACTORY = 'Factory',
  ADMIN = 'Admin',
  PM = 'PM'
}

export enum TaskVisibility {
  PUBLIC = 'Public',
  SEMI_PRIVATE = 'Semi-Private',
  PRIVATE = 'Private'
}

export interface ProjectRole {
  id: string;
  name: string; // e.g., "Product Manager", "QA Lead", "Designer"
  organization: 'Brand' | 'Factory'; // Which side this role belongs to
  team?: string; // Optional team assignment (Design, Sourcing, etc.)
  description?: string;
  createdAt: number;
}

export enum PhaseStatus {
  NOT_STARTED = 'Not Started',
  ACTIVE = 'Active',
  LOCKED = 'Locked',
  COMPLETED = 'Completed'
}

export type TaskStatus = "BLOCKED" | "NOT_STARTED" | "IN_PROGRESS" | "COMPLETE";

export type TicketStatus = "OPEN" | "IN_PROGRESS" | "RESOLVED" | "CLOSED";
export type TicketPriority = "LOW" | "MEDIUM" | "HIGH" | "URGENT";

export type ThemeMode = 'light' | 'dark' | 'system';

export interface Comment {
  id: string;
  authorId: string;
  authorName: string;
  text: string;
  timestamp: number;
  attachments?: string[];
  isRead?: boolean;
}

export interface Ticket {
  id: string;
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  requesterId: string;
  assigneeId: string;
  projectId?: string;  // Optional project link
  taskId?: string;     // Optional task link
  createdAt: number;
  updatedAt: number;
}

export interface TaskTemplate {
  name: string;
  avgDays: number;
  count: number;
}

export interface TemplatePhase {
  id: string;
  name: string;
  sequence: number;
}

export interface Template {
  id: string;
  title: string;
  description: string;
  version: string;
  brandOnly: boolean;
  phases: TemplatePhase[];
  placeholders: string[];
  createdAt: number;
  updatedAt: number;
}

export interface CalendarEvent {
  id: string;
  title: string;
  details: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  collaboratorIds: string[]; // Uses ResourcePicker format: "USR:id" | "TEAM:name" | "ORG:name"
  color?: string;
}

export interface CustomCalendar {
  id: string;
  name: string;
  description: string;
  creatorId: string;
  editorIds: string[];
  viewerIds: string[];
  events: CalendarEvent[];
  updatedAt: number;
}

export interface Task {
  id: string;
  x: number;
  y: number;
  parentId?: string;
  childLeftIds: string[];
  childRightIds: string[];
  childBottomId?: string;
  dependsOn?: string[];
  title: string;
  description?: string;
  notes?: string;
  status: TaskStatus;
  pct?: number;
  phaseId: string;
  projectId?: string;
  templateId?: string;
  isCritical: boolean;
  duration?: number;
  daysToComplete?: number;
  totalDaysExpected?: number;
  actualDaysTaken?: number;
  startedAt?: number;
  completedAt?: number;
  startDate?: string;
  endDate?: string;
  assigneeIds?: string[]; // For project tasks (actual users)
  assignedRoleIds?: string[]; // For template tasks (role references)
  collaborators?: string[];
  audience?: 'Brand' | 'Factory' | 'Both';
  visibility?: TaskVisibility;
  viewerIds?: string[]; // User IDs who can view private/semi-private tasks
  viewerType?: 'Factory' | 'Brand' | 'Assignee' | 'Team' | 'Custom'; // Quick filter or custom selection
  hasSubtimeline?: boolean;
  comments?: Comment[];
  teams?: string[];
  files?: string[];
  lastActivityAt: number; 
  lastNudgedAt?: number;
  followUpDismissedAt?: number;
  // Client-only fields (not persisted to Firebase)
  _firebaseSynced?: boolean; // Track if task exists in Firebase
  _localOnly?: boolean; // For template draft mode editing
}

export interface Phase {
  id: string;
  name: string;
  status: PhaseStatus;
  projectId?: string;
  templateId?: string;
  gatePassed: boolean;
  isPrivate?: boolean; 
}

export interface Project {
  id: string;
  name: string;
  code: string;
  brand: string;
  factory?: string;
  brandPMId?: string;    // Brand PM user ID
  factoryPMId?: string;  // Factory PM user ID
  status: 'Green' | 'Yellow' | 'Red';
  progress: number;
  startDate: string;
  launchDate: string;
  activePhaseId: string;
  isPrivate?: boolean;
  isArchived?: boolean;      // Archive status
  archivedAt?: number;       // When archived (timestamp)
}

export type FileCategory = 'Render' | 'Packaging' | 'Education' | 'Manual' | 'Invoice' | 'Other';

export interface FileAsset {
  id: string;
  name: string;
  type: 'image' | 'document' | '3d' | 'invoice';
  category: FileCategory;
  size: string;
  uploadedBy: string;
  uploadDate: string;
  tags: string[];
  projectId: string;
  audience: 'All' | 'Brand' | 'Factory';
  thumbnailUrl?: string;
  
  // Task relationship
  taskId?: string;                    // If uploaded to a task
  
  // Permission system (mirrors Task visibility)
  visibility?: 'Public' | 'Semi-Private' | 'Private'; // Default: 'Public'
  viewerIds?: string[];               // User IDs who can view
  viewerType?: 'Factory' | 'Brand' | 'Assignee' | 'Team' | 'Custom';
  viewerTeams?: string[];             // Team names that can view
  
  // Search optimization fields
  searchText?: string;                // Combined lowercase searchable text
  normalizedName?: string;            // Cleaned filename for matching
  uploadTimestamp?: number;           // Unix timestamp for "recent" queries
  semanticTags?: string[];            // Auto-generated tags
  
  // Content extraction (for Mega Brain)
  extractedContent?: string;          // First 50,000 chars of document
  contentExtracted?: boolean;         // Extraction completed flag
  contentExtractionError?: string;    // Error message if failed
  storagePath?: string;               // Full Firebase Storage path
  
  // Denormalized fields for Firestore rules optimization (no nested gets)
  assigneeIds?: string[];             // Copied from linked task
  uploaderRole?: Role;                // Role of uploader at upload time
  projectBrand?: string;              // Brand from project
  projectFactory?: string;           // Factory from project
  extractionInProgress?: boolean;     // Lock flag for race condition protection
  extractionStartedAt?: number;        // Timestamp when extraction started
}

export interface Notification {
  id: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: number;
}

export interface AlertPrefs {
  enabled: boolean;
  emailEnabled: boolean; // Master AWS SES toggle
  notifyMentions: boolean;
  notifyTickets: boolean;
  notifyImportantNotStarted: boolean;
  notifyUpcomingStart: boolean; // 1-2 days before est start
  notifyDueSoon: boolean; // 2 business days before due date
  digestFrequency: 'IMMEDIATE' | 'DAILY' | 'WEEKLY';
  leadTimeDays: number;
  slackThreshold: number;
}

export interface PendingUser {
  id: string;
  name: string;
  email: string;
  avatar: string;
  requestedAt: number;
  source: string; // e.g. "Invite Link", "SSO"
}

export interface AppState {
  user: User | null;
  projects: Project[];
  phases: Phase[];
  tasks: Task[];
  templates: Template[];
  tickets: Ticket[];
  files: FileAsset[];
  users: User[];
  pendingUsers: PendingUser[];
  taskTemplates: TaskTemplate[];
  notifications: Notification[];
  followedProjectIds: string[];
  alertPrefs: AlertPrefs;
  themeMode: ThemeMode;
  customCalendars: CustomCalendar[];
  selectedCustomCalendarIds: string[];
  projectRoles: ProjectRole[];
  login: (email: string, password?: string) => Promise<void>;
  logout: () => Promise<void>;
  loadUserData: () => Promise<void>;
  mode: 'mock' | 'firebase';
  isLoading: boolean;
  initializeFirebase: () => Promise<void>;
  updateUser: (updates: Partial<User>) => Promise<void>;
  updateProject: (projectId: string, updates: Partial<Project>) => Promise<void>;
  archiveProject: (projectId: string) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  restoreProject: (projectId: string) => Promise<void>;
  updatePhase: (phaseId: string, updates: Partial<Phase>) => Promise<void>;
  updateTask: (taskId: string, updates: Partial<Task>) => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;
  duplicateTask: (taskId: string) => Promise<void>;
  addTaskDown: (parentId: string) => Promise<void>;
  addTaskHorizontal: (parentId: string, dir: -1 | 1) => Promise<void>;
  addGlobalParallelTask: (projectId?: string, phaseId?: string, parentTaskId?: string, templateId?: string) => Promise<void>;
  addTicket: (ticket: Omit<Ticket, 'id' | 'createdAt' | 'updatedAt' | 'status'>) => Promise<void>;
  updateTicket: (ticketId: string, updates: Partial<Ticket>) => Promise<void>;
  advancePhase: (phaseId: string) => Promise<void>;
  addNotification: (message: string, type?: Notification['type']) => void;
  removeNotification: (id: string) => void;
  reorganize: (projectId?: string, phaseId?: string, templateId?: string) => void;
  addUser: (user: Omit<User, 'id'>) => User;
  approveUser: (pendingUserId: string, config: { role: Role, company: string, department: string, assignedProjectIds: string[] }) => void;
  rejectUser: (pendingUserId: string) => void;
  addTemplate: (title: string, description: string) => Promise<string>;
  updateTemplate: (templateId: string, updates: Partial<Template>) => Promise<void>;
  deleteTemplate: (templateId: string) => Promise<void>;
  applyTemplate: (templateId: string, projectName: string, fieldValues: Record<string, string>) => Promise<void>;
  addTemplatePhase: (templateId: string) => Promise<void>;
  createBlankProject: (data: { name: string; pmId: string; factory?: string; goalLaunchDate?: string }) => Promise<string>;
  addProjectPhase: (projectId: string, phaseName?: string) => Promise<void>;
  toggleFollowProject: (projectId: string) => Promise<void>;
  updateAlertPrefs: (prefs: Partial<AlertPrefs>) => Promise<void>;
  setThemeMode: (mode: ThemeMode) => Promise<void>;
  regenerateSyncToken: () => void;
  markCommentAsRead: (taskId: string, commentId: string) => Promise<void>;
  replyToComment: (taskId: string, originalCommentId: string, replyText: string) => Promise<void>;
  nudgeTask: (taskId: string) => void;
  dismissFollowUp: (taskId: string) => void;
  setTasks: (tasks: Task[]) => void;
  checkAccess: (projectId: string) => boolean;
  addCalendar: (name: string) => string;
  updateCalendar: (id: string, updates: Partial<CustomCalendar>) => void;
  deleteCalendar: (id: string) => void;
  toggleCalendarVisibility: (id: string) => void;
  addProjectRole: (role: Omit<ProjectRole, 'id' | 'createdAt'>) => Promise<string>;
  updateProjectRole: (roleId: string, updates: Partial<ProjectRole>) => Promise<void>;
  deleteProjectRole: (roleId: string) => Promise<void>;
}

export interface User {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean; // For AWS SES verification status
  role: Role;
  avatar: string;
  avatarColorScheme?: string; // 'blue' | 'purple' | 'emerald' | 'amber' | 'rose'
  company?: string;
  department?: string;
  assignedProjectIds?: string[];
  calendarSyncToken?: string;
  
  // User preferences
  followedProjectIds?: string[];
  alertPrefs?: AlertPrefs;
  selectedCustomCalendarIds?: string[];
  themeMode?: ThemeMode;
}
